import { useState, useEffect } from 'react';
import { X, Key, Plus, Eye, EyeOff, CreditCard as Edit, Trash2, Copy, Check, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../../utils/supabaseStorage';
import { parseAdminStaffSession } from '../../utils/adminSession';
import { LoadingSpinner } from '../LoadingSpinner';

interface ApiKey {
  id: string;
  provider: string;
  name: string;
  api_key: string;
  is_active: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  hy_user_id?: string | null;
  users?: { hy_name?: string; hy_user_id?: string } | null;
}

interface StaffPickRow {
  hy_user_id: string;
  hy_name: string;
  role: string;
}

interface ApiKeysModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApiKeysModal = ({ isOpen, onClose }: ApiKeysModalProps) => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [adminUsers, setAdminUsers] = useState<StaffPickRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [accessRecord, setAccessRecord] = useState<any>(null);

  // Form state for creating/editing API keys
  const [formData, setFormData] = useState({
    provider: '',
    name: '',
    api_key: '',
    is_active: true,
    assigned_hy_user_id: '',
  });

  useEffect(() => {
    if (isOpen) {
      loadApiKeys();
      loadAdminUsers();
      loadAccessRecord();
    }
  }, [isOpen]);

  const loadAccessRecord = () => {
    setAccessRecord(parseAdminStaffSession());
  };

  const loadAdminUsers = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('hy_user_id, hy_name, role')
        .in('role', ['admin', 'superadmin'])
        .order('hy_name', { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      setAdminUsers(data || []);
    } catch (err) {
      console.error('Error loading admin users:', err);
    }
  };

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);

      const staff = parseAdminStaffSession();

      let query = supabase
        .from('api_keys')
        .select('*, users ( hy_name, hy_user_id )')
        .order('created_at', { ascending: false });

      if (staff && staff.role !== 'superadmin') {
        query = query.eq('hy_user_id', staff.hy_user_id);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setApiKeys((data || []) as ApiKey[]);
    } catch (err) {
      console.error('Error loading API keys:', err);
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!formData.provider.trim() || !formData.name.trim() || !formData.api_key.trim()) {
      setError('All fields are required');
      return;
    }

    try {
      setError(null);

      const staff = parseAdminStaffSession();
      if (!staff?.hy_user_id) {
        setError('Admin session missing');
        return;
      }

      let targetHy = staff.hy_user_id;
      if (staff.role === 'superadmin' && formData.assigned_hy_user_id.trim()) {
        targetHy = formData.assigned_hy_user_id.trim();
      }

      const { error: insertError } = await supabase
        .from('api_keys')
        .insert({
          provider: formData.provider.trim(),
          name: formData.name.trim(),
          api_key: formData.api_key.trim(),
          is_active: formData.is_active,
          hy_user_id: targetHy,
        });

      if (insertError) {
        throw insertError;
      }

      setSuccess('API key created successfully');
      setShowCreateForm(false);
      setFormData({ provider: '', name: '', api_key: '', is_active: true, assigned_hy_user_id: '' });
      await loadApiKeys();
    } catch (err) {
      console.error('Error creating API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    }
  };

  const handleUpdateKey = async () => {
    if (!editingKey || !formData.provider.trim() || !formData.name.trim()) {
      setError('Provider and name are required');
      return;
    }

    try {
      setError(null);

      const staff = parseAdminStaffSession();

      const updateData: Record<string, unknown> = {
        provider: formData.provider.trim(),
        name: formData.name.trim(),
        is_active: formData.is_active,
      };

      if (formData.api_key.trim() && formData.api_key !== '••••••••••••••••') {
        updateData.api_key = formData.api_key.trim();
      }

      if (
        staff?.role === 'superadmin' &&
        formData.assigned_hy_user_id.trim() &&
        formData.assigned_hy_user_id.trim() !== (editingKey.hy_user_id || '')
      ) {
        updateData.hy_user_id = formData.assigned_hy_user_id.trim();
      }

      const { error: updateError } = await supabase
        .from('api_keys')
        .update(updateData)
        .eq('id', editingKey.id);

      if (updateError) {
        throw updateError;
      }

      setSuccess('API key updated successfully');
      setEditingKey(null);
      setFormData({ provider: '', name: '', api_key: '', is_active: true, assigned_hy_user_id: '' });
      await loadApiKeys();
    } catch (err) {
      console.error('Error updating API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to update API key');
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(keyId);
      setError(null);

      const { error: deleteError } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', keyId);

      if (deleteError) {
        throw deleteError;
      }

      setSuccess('API key deleted successfully');
      await loadApiKeys();
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete API key');
    } finally {
      setDeleting(null);
    }
  };

  const toggleKeyVisibility = (keyId: string) => {
    setRevealedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const maskApiKey = (key: string): string => {
    if (key.length <= 8) return '••••••••';
    return key.substring(0, 4) + '••••••••••••••••' + key.substring(key.length - 4);
  };

  const startEdit = (apiKey: ApiKey) => {
    setEditingKey(apiKey);
    setFormData({
      provider: apiKey.provider,
      name: apiKey.name,
      api_key: '••••••••••••••••', // Masked placeholder
      is_active: apiKey.is_active,
      assigned_hy_user_id: apiKey.hy_user_id || '',
    });
    setShowCreateForm(true);
  };

  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingKey(null);
    setFormData({ provider: '', name: '', api_key: '', is_active: true, assigned_hy_user_id: '' });
    setError(null);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-base/90 z-modal-content flex items-center justify-center p-4">
      <div className="bg-surface border border-subtle rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-subtle">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-status-warning rounded-xl flex items-center justify-center">
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-th-primary">API Key Management</h2>
              <p className="text-sm text-th-secondary">
                Manage your third-party API keys and integrations
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Success/Error Messages */}
          {success && (
            <div className="p-4 bg-status-success/10 border border-status-success/30 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-status-success" />
                  <span className="text-status-success font-medium">{success}</span>
                </div>
                <button onClick={clearMessages} className="text-status-success hover:text-status-success/80">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-status-error" />
                  <span className="text-status-error">{error}</span>
                </div>
                <button onClick={clearMessages} className="text-status-error hover:text-status-error/80">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Create/Edit Form */}
          {showCreateForm && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-th-primary">
                  {editingKey ? 'Edit API Key' : 'Create New API Key'}
                </h3>
                <button
                  onClick={cancelForm}
                  className="text-th-secondary hover:text-th-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-th-primary">
                    Provider *
                  </label>
                  <input
                    type="text"
                    value={formData.provider}
                    onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value }))}
                    placeholder="OpenAI, Anthropic, etc."
                    className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-th-primary">
                    Key Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Production API Key"
                    className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-medium text-th-primary">
                    API Key {editingKey && '(leave empty to keep current)'}
                  </label>
                  <input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder={editingKey ? 'Enter new API key or leave empty' : 'sk-...'}
                    className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all font-mono"
                  />
                  <p className="text-xs text-th-secondary">
                    {editingKey
                      ? 'Only enter a value if you want to update the API key'
                      : 'Enter your API key (will be encrypted and stored securely)'
                    }
                  </p>
                </div>

                {/* Admin Assignment (Superadmin Only) */}
                {accessRecord?.role === 'superadmin' && (
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-medium text-th-primary">
                      Assign to staff user
                    </label>
                    <select
                      value={formData.assigned_hy_user_id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, assigned_hy_user_id: e.target.value }))}
                      className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary focus:border-brand focus:outline-none transition-all"
                    >
                      <option value="">Default (your account)</option>
                      {adminUsers.map((admin) => (
                        <option key={admin.hy_user_id} value={admin.hy_user_id}>
                          {admin.hy_name} ({admin.role === 'superadmin' ? 'Superadmin' : 'Admin'})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-th-secondary">
                      Superadmin only: attach this key to another admin or superadmin by their user id.
                    </p>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4 text-brand bg-elevated border-subtle rounded focus:ring-brand focus:ring-2"
                    />
                    <span className="text-th-primary">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center space-x-3 mt-6">
                <button
                  onClick={editingKey ? handleUpdateKey : handleCreateKey}
                  className="px-6 py-3 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 transition-all"
                >
                  {editingKey ? 'Update Key' : 'Create Key'}
                </button>
                <button
                  onClick={cancelForm}
                  className="px-6 py-3 bg-elevated border border-subtle text-th-secondary rounded-lg font-medium hover:text-th-primary transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* API Keys List */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-th-primary">API Keys</h3>
              {!showCreateForm && (
                <button
                  onClick={() => {
                    setShowCreateForm(true);
                    setEditingKey(null);
                    setFormData({ provider: '', name: '', api_key: '', is_active: true, assigned_hy_user_id: '' });
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add API Key</span>
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <LoadingSpinner size="lg" />
                <p className="text-th-secondary mt-4">Loading API keys...</p>
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-12">
                <Key className="w-16 h-16 text-th-secondary mx-auto mb-4" />
                <h4 className="text-th-primary font-medium mb-2">No API Keys</h4>
                <p className="text-th-secondary text-sm mb-4">
                  Add your first API key to start integrating with third-party services
                </p>
                <button
                  onClick={() => {
                    setShowCreateForm(true);
                    setEditingKey(null);
                    setFormData({ provider: '', name: '', api_key: '', is_active: true, assigned_hy_user_id: '' });
                  }}
                  className="button-primary"
                >
                  Add First API Key
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((apiKey) => {
                  const isRevealed = revealedKeys.has(apiKey.id);
                  const isCopied = copiedKey === apiKey.id;
                  const isDeleting = deleting === apiKey.id;

                  return (
                    <div
                      key={apiKey.id}
                      className="bg-elevated border border-subtle rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-th-primary font-medium">
                              {apiKey.name}
                            </h4>
                            <span className="text-xs px-2 py-1 bg-accent/20 text-accent rounded-full">
                              {apiKey.provider}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              apiKey.is_active
                                ? 'bg-status-success/20 text-status-success'
                                : 'bg-status-error/20 text-status-error'
                            }`}>
                              {apiKey.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          {/* API Key Display */}
                          <div className="flex items-center space-x-2 mb-3">
                            <code className="flex-1 bg-surface border border-subtle rounded px-3 py-2 text-field font-mono text-sm">
                              {isRevealed ? apiKey.api_key : maskApiKey(apiKey.api_key)}
                            </code>
                            <button
                              onClick={() => toggleKeyVisibility(apiKey.id)}
                              className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded hover:bg-overlay"
                              title={isRevealed ? 'Hide API key' : 'Show API key'}
                            >
                              {isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => copyToClipboard(apiKey.api_key, apiKey.id)}
                              className="p-2 text-th-secondary hover:text-brand transition-colors rounded hover:bg-overlay"
                              title="Copy API key"
                            >
                              {isCopied ? <Check className="w-4 h-4 text-status-success" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>

                          {/* Usage Stats */}
                          <div className="flex items-center space-x-4 text-xs text-th-secondary">
                            <span>Usage: {apiKey.usage_count.toLocaleString()}</span>
                            <span>Created: {new Date(apiKey.created_at).toLocaleDateString()}</span>
                            {apiKey.last_used_at && (
                              <span>Last used: {new Date(apiKey.last_used_at).toLocaleDateString()}</span>
                            )}
                          </div>

                          {/* Assigned Admin Info */}
                          {(apiKey.users?.hy_name || apiKey.hy_user_id) && (
                            <div className="mt-2 p-2 bg-accent/10 border border-accent/20 rounded">
                              <p className="text-accent text-xs font-medium">
                                Owner: {apiKey.users?.hy_name || apiKey.hy_user_id}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => startEdit(apiKey)}
                            className="p-2 text-th-secondary hover:text-accent transition-colors rounded hover:bg-accent/10"
                            title="Edit API key"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteKey(apiKey.id)}
                            disabled={isDeleting}
                            className="p-2 text-th-secondary hover:text-status-error transition-colors rounded hover:bg-status-error/10 disabled:opacity-50"
                            title="Delete API key"
                          >
                            {isDeleting ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Provider Examples */}
          {!showCreateForm && (
            <div className="bg-elevated border border-subtle rounded-xl p-6">
              <h4 className="text-lg font-semibold text-th-primary mb-4">
                Common API Providers
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h5 className="text-th-primary font-medium">AI Services</h5>
                  <div className="space-y-1 text-sm text-th-secondary">
                    <p>• OpenAI (GPT, DALL-E)</p>
                    <p>• Anthropic (Claude)</p>
                    <p>• Google (Gemini)</p>
                    <p>• FAL AI (Image/Video Generation)</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <h5 className="text-th-primary font-medium">Other Services</h5>
                  <div className="space-y-1 text-sm text-th-secondary">
                    <p>• Stripe (Payments)</p>
                    <p>• SendGrid (Email)</p>
                    <p>• Twilio (SMS)</p>
                    <p>• AWS S3 (Storage)</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Notice */}
          <div className="bg-status-warning/10 border border-status-warning/20 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="w-5 h-5 text-status-warning" />
              <h4 className="text-status-warning font-medium">Security Notice</h4>
            </div>
            <div className="text-th-secondary text-sm space-y-1">
              <p>• API keys are stored encrypted in the database</p>
              <p>• Keys are masked in the interface for security</p>
              <p>• Only reveal keys when absolutely necessary</p>
              <p>• Regularly rotate your API keys for better security</p>
              <p>• Deactivate unused keys to prevent unauthorized access</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-subtle">
          <div className="text-sm text-th-secondary">
            {apiKeys.length > 0 ? (
              `${apiKeys.length} API key${apiKeys.length !== 1 ? 's' : ''} • ${apiKeys.filter(k => k.is_active).length} active`
            ) : (
              'Securely manage your API keys and third-party integrations'
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-elevated border border-subtle text-th-secondary rounded-lg font-medium hover:text-th-primary hover:border-brand/50 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
